const EmbeddedComponents = require('@/vue/EmbeddedComponents');
const EventNames = require('@/lib/Events/EventNames');
const Payment = require('@/lib/Payments/Payment');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const presentSoldGiftVoucherRefFilter = require('@/filters/presentSoldGiftVoucherRef');
const redeemSoldGiftVoucher = require('@/lib/GiftVouchers/redeemSoldGiftVoucher');

/**
 * This is the controller for the inner part of the payment modal for the payment method of "Gift Voucher".
 *
 * @param $filter
 * @param $rootScope
 * @param $scope
 * @param $timeout
 * @param {ExternalCartCalculator} externalCartCalculator
 * @param {PaymentMethodService} paymentMethodService
 */
function GiftVoucherPaymentCtrl(
    $filter,
    $rootScope,
    $scope,
    $timeout,
    externalCartCalculator,
    paymentMethodService,
) {
    $scope.$on('$destroy', () => {
        window.rootVueStore.commit('removeActiveEmbeddedComponent', EmbeddedComponents.GIFT_VOUCHER_PAYMENT_METHOD_COMPONENT);
    });

    $scope.$on(EventNames.GIFT_VOUCHER_SELECTED_FOR_USE, (event, soldGiftVoucher) => {
        $scope.useVoucher(soldGiftVoucher);
    });

    const thisPaymentMethodRef = PaymentMethodRef.GIFT_VOUCHER;
    $scope.thisPaymentMethod = paymentMethodService.getByRef(thisPaymentMethodRef);

    /**
     * Keep track of any recently used vouchers.
     * This avoids repeat usage of same voucher prior to DT server getting updated.
     * Keyed by ID.
     *
     * @type {Object<SoldGiftVoucher>}
     */
    let voucherCache = {};

    /**
     * Whether we're currently in the process of applying a voucher to the cart
     *
     * @type {boolean}
     */
    $scope.applying = false;

    /**
     * Watch out - CartService, NOT a Cart!
     *
     * @type {CartService}
     */
    const cartService = $scope.cart;

    const showError = (errorMessage) => {
        if (errorMessage instanceof Error) {
            errorMessage = errorMessage.message;
        }
        $scope.applying = false;
        $scope.$parent.setError(errorMessage);
    };

    const clear = () => {
        $timeout(() => {
            $scope.applying = false;
            $scope.clearError();
        });
    };

    // Initialise our state
    clear();

    /**
     * @param {string} ref
     */
    $scope.searchVoucherCacheByRef = (ref) => {
        const presentedRef = presentSoldGiftVoucherRefFilter(ref);
        return Object.values(voucherCache).find(
            (soldGiftVoucher) => presentSoldGiftVoucherRefFilter(soldGiftVoucher.ref) === presentedRef
        );
    };

    $scope.$on(EventNames.PAYMENT_METHOD_CHANGED, function (event, newPaymentMethod) {
        const newPaymentMethodRef = newPaymentMethod?.ref;
        clear();
        if (newPaymentMethodRef === thisPaymentMethodRef) {
            // Vue <EposAppRoot> watches for this to enable the teleported Vue <GiftVoucherPaymentMethodComponent>.
            window.rootVueStore.commit('addActiveEmbeddedComponent', { name: EmbeddedComponents.GIFT_VOUCHER_PAYMENT_METHOD_COMPONENT });
        } else {
            window.rootVueStore.commit('removeActiveEmbeddedComponent', EmbeddedComponents.GIFT_VOUCHER_PAYMENT_METHOD_COMPONENT);
        }
    });

    $scope.$on('payment-modal.closing', () => {
        // If the payment cycle is complete, or the user is cancelling, clear the cache
        voucherCache = {};
    });

    $scope.$on('payment-modal.cancel-pressed', () => {
        // Experience Gift Vouchers act as discounts and need to be removed when payments are abandoned and modal closed.
        $scope.cart.clearPaymentDiscounts();
        // Now re-calculate the cart total.
        $scope.cart.refreshExternalCalculations(true);
    });

    /**
     * This method is triggered by the 'Use Voucher' button.
     *
     * @param {SoldGiftVoucher} soldGiftVoucher
     *
     * @return {Promise}
     */
    $scope.useVoucher = async (soldGiftVoucher) => {
        $scope.clearError();
        $scope.applying = true;
        if (cartService.getRemainingBalance() > 0) {
            if (!soldGiftVoucher) {
                return showError($filter('lang')('GIFT_VOUCHERS.SEARCH_ERROR'));
            }

            if ($scope.cart.containsDiscountsExcludingExperienceVouchers() && soldGiftVoucher.giftVoucher.isExperienceType()) {
                return showError($filter('lang')('GIFT_VOUCHERS.DISCOUNT_ERROR'));
            }

            // Load voucher from cache if available (to take into account prior applications)
            if (voucherCache[soldGiftVoucher.ID] !== undefined) {
                soldGiftVoucher = voucherCache[soldGiftVoucher.ID];
            }

            if (!soldGiftVoucher.isRedeemable) {
                return showError($filter('lang')('GIFT_VOUCHERS.REDEEM_ERROR'));
            }
            // If partial payments aren't allowed and the voucher doesn't have enough to cover the balance, exit
            if (
                !cartService.allowPartialPayments()
                && soldGiftVoucher.isBalanceType
                && soldGiftVoucher.currentBalance < cartService.getRemainingBalance()
            ) {
                return showError($filter('lang')('PAYMENT.PARTIAL_PAYMENT_VOUCHER_ERROR'));
            }

            const amountTendered = soldGiftVoucher.isBalanceType
                ? Math.min(cartService.getRemainingBalance(), soldGiftVoucher.currentBalance)
                : soldGiftVoucher.currentBalance;

            let payment = new Payment();
            payment.setPaymentMethod($scope.thisPaymentMethod);
            payment.setTendered(amountTendered);
            payment.setThirdPartyID(cartService.generatePaymentThirdPartyID());
            payment.transactionRef = soldGiftVoucher.ref;

            let payments = cartService.payments;
            payments.push(payment);
            payments = [...new Set(payments)];

            // If this is an experience voucher, ask the API to tweak the cart contents as required to
            // adjust the price of the item being purchased to reflect the amount originally paid for the
            // voucher (so that the taxable amount is correct).
            if (soldGiftVoucher.isExperienceType) {
                try {
                    console.log('trying to refresh cart');
                    try {
                        // This gets called every time we have searched for a voucher and then click 'Use Voucher'.
                        await externalCartCalculator.recalculate(cartService, payments);
                    } catch (error) {
                        $scope.cart.payments.pop();
                        $scope.$broadcast('payment-modal.payment-removed');
                        return showError(error);
                    } finally {
                        await cartService.recalculateAfterModifyingCartContents();
                    }

                    if (cartService.currentCart.requiresReservation()) {
                        await cartService.createReservation();
                    }

                    const lastDiscount = $scope.cart.getDiscountSummary().pop();
                    if (lastDiscount && lastDiscount.reason === PaymentMethodRef.GIFT_VOUCHER) {
                        $scope.$apply(function () {
                            $scope.cartItemDiscounts.push(lastDiscount);
                        });
                    }
                } catch (e) {
                    console.error('Error while applying experience voucher', e);
                    // If there is a problem with one of the above requests we can't proceed with the
                    // payment as we don't know how much our experience voucher is 'worth', and this is
                    // required in order to set our payment amount.
                    return showError(
                        $filter('lang')('GIFT_VOUCHERS.CONFIRMATION_ERROR',
                            {
                                message: e.message,
                            })
                    );
                }
                // cart should now have been updated
            }

            // Update the sold gift voucher
            try {
                soldGiftVoucher = redeemSoldGiftVoucher(soldGiftVoucher, amountTendered);
            } catch (e) {
                return showError(e.message);
            }
            voucherCache[soldGiftVoucher.ID] = soldGiftVoucher;

            $scope.addPaymentAndAutoFinish(payment);

            // Clear for next use.
            clear();
        }

        return showError($filter('lang')('GIFT_VOUCHERS.REFUND_ERROR'));
    };
}

module.exports = GiftVoucherPaymentCtrl;
